import React from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import GameLayout from '../layouts/GameLayout';
import LobbyPage from '../pages/LobbyPage';
import WaitingRoomPage from '../pages/WaitingRoomPage';
import GamePage from '../pages/GamePage';
import DashboardPage from '../pages/DashboardPage';
import LoginPage from '../pages/LoginPage';
import RegisterPage from '../pages/RegisterPage';
import VerificationPage from '../pages/VerificationPage';
import ForgotPasswordPage from '../pages/ForgotPasswordPage';
import ResetPasswordPage from '../pages/ResetPasswordPage';
import EmailVerification from '../components/EmailVerification';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from '../contexts/AuthContext';

const AppRouter = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600'></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Default route - redirect based on authentication status */}
      <Route
        path='/'
        element={isAuthenticated ? <Navigate to='/dashboard' replace /> : <Navigate to='/login' replace />}
      />

      {/* Authentication routes - accessible without authentication */}
      <Route path='/login' element={<LoginPage />} />
      <Route path='/register' element={<RegisterPage />} />
      <Route path='/verify' element={<VerificationPage />} />
      <Route path='/verify-email' element={<EmailVerificationWrapper />} />
      <Route path='/forgot-password' element={<ForgotPasswordPage />} />
      <Route path='/reset-password' element={<ResetPasswordPage />} />

      {/* Protected Dashboard routes */}
      <Route
        path='/dashboard'
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DashboardPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Protected Game routes with layout */}
      <Route
        path='/game-lobby/*'
        element={
          <ProtectedRoute>
            <GameLayout />
          </ProtectedRoute>
        }>
        <Route index element={<Navigate to='lobby' replace />} />
        <Route path='lobby' element={<LobbyPage />} />
        <Route path='waiting' element={<WaitingRoomPage />} />
        <Route path='waiting/:roomId' element={<WaitingRoomPage />} />
        <Route path='game' element={<GamePage />} />
        <Route path='game/:roomId' element={<GamePage />} />
      </Route>

      {/* Fallback route - redirect based on authentication status */}
      <Route
        path='*'
        element={isAuthenticated ? <Navigate to='/dashboard' replace /> : <Navigate to='/login' replace />}
      />
    </Routes>
  );
};

// Wrapper component for email verification
const EmailVerificationWrapper = () => {
  const [token, setToken] = React.useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    // Get token from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const verificationToken = urlParams.get('token');
    setToken(verificationToken);
  }, []);

  const handleVerificationComplete = (success) => {
    if (success) {
      // Redirect to login page after successful verification
      navigate('/login');
    } else {
      // Redirect to register page if verification failed
      navigate('/register');
    }
  };

  if (!token) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <div className='bg-white rounded-lg shadow-xl p-8 text-center'>
          <h2 className='text-2xl font-bold text-gray-900 mb-4'>Invalid Verification Link</h2>
          <p className='text-gray-600 mb-6'>The verification link is invalid or missing.</p>
          <a
            href='/register'
            className='bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-medium transition-colors'>
            Go to Registration
          </a>
        </div>
      </div>
    );
  }

  return <EmailVerification token={token} onVerificationComplete={handleVerificationComplete} />;
};

export default AppRouter;
